"use client";

import { useState, useEffect, useCallback } from "react";
import { Bug, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import FeedbackForm from "@/components/FeedbackForm";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useLocation, useNavigate } from "react-router-dom";
import { usePageContext, type PageContext } from "@/hooks/usePageContext";
import { isWaitlistCurtainEnabled, isWaitlistGatedPath } from "@/lib/waitlistCurtain";

export function FeedbackWidget() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [user, setUser] = useState<{ id: string } | null>(null);
  const { toast } = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const pageContext = usePageContext();
  const currentPath = location.pathname;

  // Store the current path when the modal is opened
  const [pathWhenOpened, setPathWhenOpened] = useState<string | null>(null);

  // Snapshot of where the user was when they opened the modal — this is the
  // context the feedback is about, even if navigation shifts underneath.
  const [contextWhenOpened, setContextWhenOpened] = useState<PageContext | null>(null);

  // Fetch user data
  useEffect(() => {
    const getUser = async () => {
      try {
        const { data } = await supabase.auth.getUser();
        setUser(data.user ? { id: data.user.id } : null);
      } catch (error) {
        console.error("Error fetching user:", error);
      }
    };

    getUser();

    const { data: authListener } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setUser(session?.user ? { id: session.user.id } : null);
      }
    );

    return () => {
      authListener?.subscription.unsubscribe();
    };
  }, []);

  // Prevent scrolling when modal is open
  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isModalOpen]);

  // Navigation protection effect - if the path changes while modal is open,
  // this will revert back to the original path
  useEffect(() => {
    if (isModalOpen && pathWhenOpened && currentPath !== pathWhenOpened) {
      // If path changed while modal is open, navigate back to original path
      navigate(pathWhenOpened, { replace: true });
    }
  }, [isModalOpen, currentPath, pathWhenOpened, navigate]);

  // Handle modal open with multiple safety measures
  const openModal = useCallback((e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    // Prevent default browser behavior
    e.preventDefault();
    
    // Stop propagation to prevent parent elements from handling click
    e.stopPropagation();
    
    // Store current path to restore it if needed
    setPathWhenOpened(currentPath);

    // Capture what the user was looking at for the feedback record
    setContextWhenOpened(pageContext);

    // Open the modal
    setIsModalOpen(true);
    
    // Push the same URL again to replace current history entry
    // This prevents the back button from closing the modal
    navigate(currentPath, { replace: true });
    
    // Return false to prevent any other handlers
    return false;
  }, [currentPath, navigate, pageContext]);

  // Handle modal close
  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setPathWhenOpened(null);
    setContextWhenOpened(null);
  }, []);

  // Modal overlay click handler
  const handleOverlayClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    closeModal();
  }, [closeModal]);

  // Prevent bubbling for modal content clicks
  const handleModalContentClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  const hideOnCurtain =
    isWaitlistCurtainEnabled() &&
    (currentPath === "/" || isWaitlistGatedPath(currentPath) || !user);

  if (hideOnCurtain) return null;

  return (
    <>
      {/* Button with multiple event safeguards */}
      <div 
        className="fixed bottom-4 right-4 z-40"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <Button
          type="button"
          size="icon"
          onClick={openModal}
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          className="h-10 w-10 rounded-full shadow-md bg-neutral-200 hover:bg-neutral-300 text-neutral-600"
          aria-label="Submit feedback"
        >
          <Bug className="h-4 w-4" />
        </Button>
      </div>

      {/* Modal with portal-like implementation */}
      {isModalOpen && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={handleOverlayClick}
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
        >
          <div 
            className="bg-white rounded-lg max-w-[550px] w-[95%] max-h-[90vh] relative" 
            onClick={handleModalContentClick}
          >
            <button 
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                closeModal();
              }}
              aria-label="Close feedback modal"
            >
              <X className="h-4 w-4" />
            </button>

            <div className="p-6 overflow-y-auto max-h-[calc(90vh-2rem)]">
              <h2 className="text-2xl font-semibold text-stone-900 mb-6">Submit Feedback</h2>
              <p className="text-stone-600 mb-6">
                We value your input! Please share your thoughts, suggestions, or report any issues 
                you've encountered.
              </p>
              
              <FeedbackForm
                onSuccess={closeModal}
                currentUser={user}
                defaultFeedbackType="Bug"
                pageContext={contextWhenOpened}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default FeedbackWidget;
